/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * What the client must present to authenticate: 'public' (nothing), 'secret' (a client secret), 'key' (an assertion signed by its published key), or 'misconfigured'. Derived by the same rule the token endpoint enforces, so it reads what the client will actually be held to rather than what it declared. 'misconfigured' means the registration contradicts itself and the client cannot authenticate at all.
 */
export const CredentialKind = {
  Public: "public",
  Secret: "secret",
  Key: "key",
  Misconfigured: "misconfigured",
} as const;
/**
 * What the client must present to authenticate: 'public' (nothing), 'secret' (a client secret), 'key' (an assertion signed by its published key), or 'misconfigured'. Derived by the same rule the token endpoint enforces, so it reads what the client will actually be held to rather than what it declared. 'misconfigured' means the registration contradicts itself and the client cannot authenticate at all.
 */
export type CredentialKind = OpenEnum<typeof CredentialKind>;

/**
 * An MCP client registered against a user-session issuer. client_secret_hash is never returned.
 */
export type UserSessionClient = {
  /**
   * How many live user_sessions this client currently holds. Counted the same way the sessions listing's active filter counts: not revoked, and the refresh token has not expired.
   */
  activeSessionCount: number;
  /**
   * The client_id. Minted by Speakeasy for a DCR registration; for a CIMD client it is the metadata document URL and equals client_id_metadata_uri.
   */
  clientId: string;
  clientIdIssuedAt: Date;
  /**
   * When the cached metadata document lapses and the next /authorize revalidates it against the host. Null for DCR clients, and null after a refresh purge until the re-read lands.
   */
  clientIdMetadataCacheExpiresAt?: Date | undefined;
  /**
   * ETag the document host returned on the last full read; sent as If-None-Match when revalidating. Null when the host offers no validator, and null for DCR clients.
   */
  clientIdMetadataEtag?: string | undefined;
  /**
   * When the metadata document was last successfully read. A 304 revalidation counts as a read, so this is not necessarily when the body was last fetched. Null for DCR clients.
   */
  clientIdMetadataFetchedAt?: Date | undefined;
  /**
   * When set, the client was resolved from a Client ID Metadata Document (CIMD) hosted at this URL rather than registered via RFC 7591 DCR. Null for DCR clients. The URL is the client's identity, so its origin -- not client_name, which the client chooses -- is the trustworthy label.
   */
  clientIdMetadataUri?: string | undefined;
  /**
   * Display name the client supplied at registration, or the client_name extracted from its metadata document. Client-controlled and unverified; do not present it as an identity.
   */
  clientName: string;
  /**
   * Null when the secret does not expire.
   */
  clientSecretExpiresAt?: Date | undefined;
  createdAt: Date;
  /**
   * What the client must present to authenticate: 'public' (nothing), 'secret' (a client secret), 'key' (an assertion signed by its published key), or 'misconfigured'. Derived by the same rule the token endpoint enforces, so it reads what the client will actually be held to rather than what it declared. 'misconfigured' means the registration contradicts itself and the client cannot authenticate at all.
   */
  credentialKind: CredentialKind;
  /**
   * The user_session_client id.
   */
  id: string;
  /**
   * Validated on every /authorize.
   */
  redirectUris: Array<string>;
  /**
   * The raw RFC 7591 token_endpoint_auth_method the client declared, for debugging against the spec. Null for a client registered before the value was recorded, which is not the same as declaring 'none' -- credential_kind resolves both cases and is what should be displayed.
   */
  tokenEndpointAuthMethod?: string | undefined;
  updatedAt: Date;
  /**
   * The owning user_session_issuer id.
   */
  userSessionIssuerId: string;
};

/** @internal */
export const CredentialKind$inboundSchema: z.ZodMiniType<
  CredentialKind,
  unknown
> = openEnums.inboundSchema(CredentialKind);

/** @internal */
export const UserSessionClient$inboundSchema: z.ZodMiniType<
  UserSessionClient,
  unknown
> = z.pipe(
  z.object({
    active_session_count: z.int(),
    client_id: z.string(),
    client_id_issued_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    client_id_metadata_cache_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    client_id_metadata_etag: z.optional(z.string()),
    client_id_metadata_fetched_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    client_id_metadata_uri: z.optional(z.string()),
    client_name: z.string(),
    client_secret_expires_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    credential_kind: CredentialKind$inboundSchema,
    id: z.string(),
    redirect_uris: z.array(z.string()),
    token_endpoint_auth_method: z.optional(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    user_session_issuer_id: z.string(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "active_session_count": "activeSessionCount",
      "client_id": "clientId",
      "client_id_issued_at": "clientIdIssuedAt",
      "client_id_metadata_cache_expires_at": "clientIdMetadataCacheExpiresAt",
      "client_id_metadata_etag": "clientIdMetadataEtag",
      "client_id_metadata_fetched_at": "clientIdMetadataFetchedAt",
      "client_id_metadata_uri": "clientIdMetadataUri",
      "client_name": "clientName",
      "client_secret_expires_at": "clientSecretExpiresAt",
      "created_at": "createdAt",
      "credential_kind": "credentialKind",
      "redirect_uris": "redirectUris",
      "token_endpoint_auth_method": "tokenEndpointAuthMethod",
      "updated_at": "updatedAt",
      "user_session_issuer_id": "userSessionIssuerId",
    });
  }),
);

export function userSessionClientFromJSON(
  jsonString: string,
): SafeParseResult<UserSessionClient, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => UserSessionClient$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'UserSessionClient' from JSON`,
  );
}
